import React from "react";
import "./DeepLinkSandbox.css";
import { generateMarriottDeepLink } from "../../../utils/generateMarriottDeepLink";

const DeepLinkSandbox = () => {
  const copyToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      alert("Deep link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const handleDeepLinkClick = (event, link) => {
    event.preventDefault();

    // App Store URLs for fallback
    const appStoreUrls = {
      ios: "https://apps.apple.com/us/app/marriott-bonvoy/id455004730",
      android: "https://play.google.com/store/apps/details?id=com.marriott.mrt",
    };

    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    let appOpened = false;
    let fallbackTimeout;

    // Cleanup function
    const cleanup = () => {
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pagehide", handlePageHide);
    };

    // Event listeners to detect if app opened
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        cleanup();
      }
    };

    const handleBlur = () => {
      appOpened = true;
      cleanup();
    };

    const handlePageHide = () => {
      appOpened = true;
      cleanup();
    };

    // Add event listeners first
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pagehide", handlePageHide);

    // Try direct navigation first (works best when app is installed)
    try {
      window.location.href = link;
    } catch (error) {
      // Direct navigation failed, clean up and go to app store
      cleanup();
      if (isIOS) {
        window.location.href = appStoreUrls.ios;
      } else if (isAndroid) {
        window.location.href = appStoreUrls.android;
      } else {
        window.open("https://www.marriott.com/marriott-bonvoy.mi", "_blank");
      }
      return;
    }

    // Set up fallback timeout for when app is not installed
    fallbackTimeout = setTimeout(() => {
      if (!appOpened) {
        cleanup();
        // App didn't open, redirect to app store
        if (isIOS) {
          window.location.href = appStoreUrls.ios;
        } else if (isAndroid) {
          window.location.href = appStoreUrls.android;
        } else {
          window.open("https://www.marriott.com/marriott-bonvoy.mi", "_blank");
        }
      }
    }, 2500);
  };

  // Comprehensive list of all deep link scenarios
  const deepLinkScenarios = [
    {
      category: "Navigation Screens",
      links: [
        {
          title: "Home Page",
          description: "Main landing page",
          link: generateMarriottDeepLink({ fid: "HP" }),
        },
        {
          title: "Search Results",
          description: "Hotel search results page",
          link: generateMarriottDeepLink({ fid: "SR" }),
        },
        {
          title: "Search with Location",
          description: "Search results for New York",
          link: generateMarriottDeepLink({
            fid: "SR",
            ct: "New York",
            st: "NY",
          }),
        },
        {
          title: "Search with Dates",
          description: "Search with check-in/out dates",
          link: generateMarriottDeepLink({
            fid: "SR",
            ct: "New York",
            st: "NY",
            ci: "12252024",
            co: "12272024",
          }),
        },
        {
          title: "Search Advanced Results",
          description: "Advanced search results",
          link: generateMarriottDeepLink({ fid: "SAR" }),
        },
        {
          title: "Find Hotels",
          description: "Hotel finder page",
          link: generateMarriottDeepLink({ fid: "FH" }),
        },
      ],
    },
    {
      category: "Hotel & Property",
      links: [
        {
          title: "Hotel Information",
          description: "Hotel details page",
          link: generateMarriottDeepLink({ fid: "HI" }),
        },
        {
          title: "Hotel with Property Code",
          description: "Specific hotel details (NYC Downtown)",
          link: generateMarriottDeepLink({ fid: "HI", m: "NYCDT" }),
        },
        {
          title: "Hotel with Dates",
          description: "Hotel details with check-in/out",
          link: generateMarriottDeepLink({
            fid: "HI",
            m: "NYCDT",
            ci: "12252024",
            co: "12272024",
          }),
        },
        {
          title: "Rate Listing",
          description: "Available rates for property",
          link: generateMarriottDeepLink({ fid: "RL" }),
        },
        {
          title: "Rate Details",
          description: "Specific rate information",
          link: generateMarriottDeepLink({ fid: "RD" }),
        },
      ],
    },
    {
      category: "Booking & Reservations",
      links: [
        {
          title: "My Bonvoy Overview",
          description: "Member dashboard",
          link: generateMarriottDeepLink({ fid: "MBO" }),
        },
        {
          title: "Your Reservations",
          description: "List of user reservations",
          link: generateMarriottDeepLink({ fid: "YR" }),
        },
        {
          title: "Check-in",
          description: "Mobile check-in flow",
          link: generateMarriottDeepLink({ fid: "CI" }),
        },
        {
          title: "Check-out",
          description: "Mobile check-out flow",
          link: generateMarriottDeepLink({ fid: "CO" }),
        },
        {
          title: "Reservation Details",
          description: "Specific reservation lookup",
          link: generateMarriottDeepLink({
            fid: "RD",
            confirmationno: "12345678",
            firstname: "John",
            lastname: "Doe",
          }),
        },
      ],
    },
    {
      category: "Special Rates & Offers",
      links: [
        {
          title: "AAA Rate",
          description: "AAA member special rate",
          link: generateMarriottDeepLink({ fid: "SR", sr: "AAA" }),
        },
        {
          title: "Marriott Bonvoy Rate",
          description: "Member exclusive rate",
          link: generateMarriottDeepLink({ fid: "SR", sr: "MMB" }),
        },
        {
          title: "Military Rate",
          description: "Military discount rate",
          link: generateMarriottDeepLink({ fid: "SR", sr: "MMF" }),
        },
        {
          title: "Senior Rate",
          description: "Senior citizen discount",
          link: generateMarriottDeepLink({ fid: "SR", sr: "MM4" }),
        },
        {
          title: "Promotion with ID",
          description: "Specific promotion offer",
          link: generateMarriottDeepLink({ fid: "SR", prid: "PRMO" }),
        },
      ],
    },
    {
      category: "Member Benefits",
      links: [
        {
          title: "Basic Member Search",
          description: "Search with Basic tier benefits",
          link: generateMarriottDeepLink({ fid: "SR", bid: "Basic" }),
        },
        {
          title: "Silver Elite Search",
          description: "Search with Silver benefits",
          link: generateMarriottDeepLink({ fid: "SR", bid: "Silver" }),
        },
        {
          title: "Gold Elite Search",
          description: "Search with Gold benefits",
          link: generateMarriottDeepLink({ fid: "SR", bid: "Gold" }),
        },
        {
          title: "Platinum Elite Search",
          description: "Search with Platinum benefits",
          link: generateMarriottDeepLink({ fid: "SR", bid: "Platinum" }),
        },
        {
          title: "Titanium Elite Search",
          description: "Search with Titanium benefits",
          link: generateMarriottDeepLink({ fid: "SR", bid: "Titanium" }),
        },
        {
          title: "Ambassador Elite Search",
          description: "Search with Ambassador benefits",
          link: generateMarriottDeepLink({ fid: "SR", bid: "Ambassador" }),
        },
      ],
    },
    {
      category: "Search Options",
      links: [
        {
          title: "Multiple Guests",
          description: "Search for 4 guests, 2 rooms",
          link: generateMarriottDeepLink({
            fid: "SR",
            guests: 4,
            rooms: 2,
            ct: "Las Vegas",
            st: "NV",
          }),
        },
        {
          title: "Airport Search",
          description: "Search near airport (JFK)",
          link: generateMarriottDeepLink({ fid: "SR", ac: "JFK" }),
        },
        {
          title: "Brand Specific",
          description: "Search specific brand (Marriott Hotels)",
          link: generateMarriottDeepLink({ fid: "SR", mb: "MR" }),
        },
        {
          title: "Flexible Dates",
          description: "Search with flexible date options",
          link: generateMarriottDeepLink({ fid: "SR", flexdates: "true" }),
        },
        {
          title: "Alternative Search",
          description: "Category-based search",
          link: generateMarriottDeepLink({ fid: "SR", altsearch: "true" }),
        },
      ],
    },
    {
      category: "Specialized Features",
      links: [
        {
          title: "In-Stay Mobile Key",
          description: "Mobile key functionality",
          link: generateMarriottDeepLink({ fid: "ISMK" }),
        },
        {
          title: "Mobile Key List Management",
          description: "Manage mobile keys",
          link: generateMarriottDeepLink({ fid: "MKLM" }),
        },
        {
          title: "Chat",
          description: "Customer service chat",
          link: generateMarriottDeepLink({ fid: "CHT" }),
        },
        {
          title: "Service Queue",
          description: "Service request queue",
          link: generateMarriottDeepLink({ fid: "SQ" }),
        },
        {
          title: "Map Directory",
          description: "Hotel map and directory",
          link: generateMarriottDeepLink({ fid: "MD" }),
        },
        {
          title: "Restaurant",
          description: "Hotel restaurant information",
          link: generateMarriottDeepLink({ fid: "RST" }),
        },
        {
          title: "Amenities & Services",
          description: "Hotel amenities listing",
          link: generateMarriottDeepLink({ fid: "AS" }),
        },
        {
          title: "Spa Services",
          description: "Spa booking and information",
          link: generateMarriottDeepLink({ fid: "SPA" }),
        },
        {
          title: "Fitness Center",
          description: "Fitness facilities information",
          link: generateMarriottDeepLink({ fid: "FIT" }),
        },
      ],
    },
    {
      category: "Advanced Features",
      links: [
        {
          title: "Join Marriott Rewards",
          description: "Loyalty program enrollment",
          link: generateMarriottDeepLink({ fid: "JMR" }),
        },
        {
          title: "Your Account",
          description: "Account management",
          link: generateMarriottDeepLink({ fid: "YA" }),
        },
        {
          title: "Account History",
          description: "Stay and points history",
          link: generateMarriottDeepLink({ fid: "AH" }),
        },
        {
          title: "Your Account Settings",
          description: "Profile and preferences",
          link: generateMarriottDeepLink({ fid: "YAS" }),
        },
        {
          title: "Profile",
          description: "User profile management",
          link: generateMarriottDeepLink({ fid: "PF" }),
        },
        {
          title: "Points Information",
          description: "Points balance and activity",
          link: generateMarriottDeepLink({ fid: "PI" }),
        },
        {
          title: "Marriott Mobile Concierge",
          description: "Concierge services",
          link: generateMarriottDeepLink({ fid: "MMC" }),
        },
        {
          title: "Wallet",
          description: "Digital wallet features",
          link: generateMarriottDeepLink({ fid: "WLL" }),
        },
        {
          title: "Ambassador Benefits",
          description: "Ambassador tier benefits",
          link: generateMarriottDeepLink({ fid: "AMB" }),
        },
        {
          title: "Redeem & Win",
          description: "Points redemption games",
          link: generateMarriottDeepLink({ fid: "RW" }),
        },
        {
          title: "Special Campaigns",
          description: "Marketing campaigns",
          link: generateMarriottDeepLink({ fid: "SC" }),
        },
        {
          title: "Member Stories",
          description: "Member testimonials",
          link: generateMarriottDeepLink({ fid: "MS" }),
        },
        {
          title: "Book Marriott Meeting",
          description: "Meeting and event booking",
          link: generateMarriottDeepLink({ fid: "BMM" }),
        },
        {
          title: "Promotions",
          description: "Current promotions listing",
          link: generateMarriottDeepLink({ fid: "PR" }),
        },
        {
          title: "Promotion Registration",
          description: "Register for promotions",
          link: generateMarriottDeepLink({ fid: "PRRG" }),
        },
        {
          title: "Promotion Details",
          description: "Specific promotion information",
          link: generateMarriottDeepLink({ fid: "PRDT" }),
        },
      ],
    },
  ];

  return (
    <div className="deeplink-sandbox">
      <div className="sandbox-header">
        <h1>Marriott Deep Link Tester</h1>
        <p>
          Test and generate Marriott mobile deep links using the utility
          function
        </p>
        <button
          onClick={(e) => handleDeepLinkClick(e, "marriott://mobile?fid=RL&bdc=Y&m=MIASB&sr=ZX0&oid=1355738")}
          className="test-btn"
          style={{ marginTop: '10px' }}
        >
          Test Sample Deep Link
        </button>
      </div>

      <div className="sandbox-content">
        {deepLinkScenarios.map((category, categoryIndex) => (
          <section key={categoryIndex} className="category-section">
            <h2>{category.category}</h2>
            <div className="links-grid">
              {category.links.map((linkItem, linkIndex) => (
                <div key={linkIndex} className="link-card">
                  <div className="link-header">
                    <h3>{linkItem.title}</h3>
                    <p>{linkItem.description}</p>
                  </div>
                  <div className="link-content">
                    <div className="link-url">
                      <a
                        href={linkItem.link}
                        className="deep-link"
                        onClick={(e) => handleDeepLinkClick(e, linkItem.link)}
                      >
                        <code>{linkItem.link}</code>
                      </a>
                    </div>
                    <div className="link-actions">
                      <button
                        onClick={(e) => handleDeepLinkClick(e, linkItem.link)}
                        className="test-btn"
                      >
                        Test Link
                      </button>
                      <button
                        onClick={() => copyToClipboard(linkItem.link)}
                        className="copy-btn"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default DeepLinkSandbox;
